/**
 * BottomComposer — always-visible edge-pinned chat composer.
 *
 * Telegram/Gemini-style: composer is permanently visible at the bottom of the
 * viewport with safe-area-inset padding. No swipe-up gesture — instead a large
 * paste / first keystroke expands the composer into a BOTTOM SHEET (top: 14vh,
 * leaving a chat strip) over a semi-transparent scrim, so the conversation stays
 * faintly visible behind it (R-008) and is dismissable by tapping the scrim,
 * swiping the handle down, the ↓ button, or ESC (R-005). Issue #1112; was a
 * full-viewport opaque takeover before (Phase 4, composer-ux-hardening).
 *
 * The `+` button opens a sectioned drawer:
 *   1. Attach — image/file picker (delegates to onAttachTap)
 *   2. Mode   — role chips (Architect/Debug/UX/etc.)
 */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import type { ComponentChildren } from 'preact';
import { PREDEFINED_ROLES } from '@voice-assistant/shared';
import type { ImageAttachment } from '@voice-assistant/shared';
import { AttachmentPreview } from '../shared-ui/AttachmentPreview';
import { useIsBusy } from '../state/composerStore';

interface Props {
  /** The full InputBar component to render (textarea + mic + send + @mention) */
  inputBar: ComponentChildren;
  /** Find-in-chat reuse (#1433): when `findActive`, this find input renders in
   *  the pill slot INSTEAD of `inputBar`, so the bottom bar becomes the find
   *  field (same thumb-zone position). Provided by App from the chatFind hook. */
  findBar?: ComponentChildren;
  /** When true, render `findBar` in place of `inputBar` and hide the `+` button. */
  findActive?: boolean;
  /** Phase 5: read-aloud playback bar reuse — when `readAloudActive`, this bar
   *  renders in the pill slot INSTEAD of `inputBar` (find takes precedence over it,
   *  Q2=a). Same single-tenant slot as find; the `+`/attachment strip are hidden in
   *  this mode too. Provided by the shell from the docTtsStore chat-read state. */
  readAloudBar?: ComponentChildren;
  /** When true, render `readAloudBar` in place of `inputBar` (unless find is active). */
  readAloudActive?: boolean;
  /** Current session id — drives the busy-state subscription. Pass null when
   *  no session is active (composer is hidden in that case anyway). */
  currentSessionId?: string | null;
  /** Currently active role key (null = none) */
  activeRole?: string | null;
  /** Called when user picks a role from the drawer's Mode section */
  onRoleChange?: (roleKey: string | null) => void;
  /** Called when user picks Attach from the drawer (delegates to host file picker) */
  onAttachTap?: () => void;
  /** Attachment state — renders AttachmentPreview strip above the input row */
  attachments?: ImageAttachment[];
  attachProcessing?: boolean;
  attachError?: string | null;
  onRemoveAttachment?: (index: number) => void;
  /** Store-driven initial expanded state — restores per-session composer expansion on mount. */
  initialExpanded?: boolean;
  /** Reports expand/collapse changes so the caller can persist to composerStore. */
  onExpandedChange?: (expanded: boolean) => void;
}

export function BottomComposer({
  inputBar, findBar, findActive = false, readAloudBar, readAloudActive = false,
  currentSessionId, activeRole, onRoleChange, onAttachTap,
  attachments, attachProcessing, attachError, onRemoveAttachment,
  initialExpanded, onExpandedChange,
}: Props) {
  // Single-tenant pill slot: find takes precedence, then read-aloud, then input.
  // The `+` / attachment strip / expand affordances belong to compose mode only.
  const composeMode = !findActive && !readAloudActive;
  // busy-to-outbound-migration Phase 1: subscribe directly to per-session
  // busy state from composerStore. BottomComposer is keyed by sessionId in
  // App.tsx so it always renders for the active session; subscribing here
  // recomputes correctly when the agent transitions through outbound modes.
  const busy = useIsBusy(currentSessionId ?? null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState(initialExpanded ?? false);
  const composerRef = useRef<HTMLDivElement | null>(null);
  // After the user explicitly collapses, don't auto-expand until the next send/clear.
  const userCollapsedRef = useRef(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Expose the composer's measured height as a CSS variable on <html> so the
  // chat scroller (`.chat-area`) can reserve bottom padding equal to it. Without
  // this, the last lines of an assistant message read out of viewport behind
  // the composer band when scrolled to bottom. Plan §1 of composer-ux-hardening.
  // SE-MUX1 covers the resulting invariant.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    const apply = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--composer-height', `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      // Don't clear the var on unmount — keeping the last known height avoids
      // a one-frame jump in chat padding if the composer remounts during route
      // change. Stale value is benign (overestimates padding, never under).
    };
  }, []);

  // InputBar dispatches this when paste/typing warrants expansion
  useEffect(() => {
    const handler = () => {
      if (!userCollapsedRef.current) { setExpanded(true); onExpandedChange?.(true); }
    };
    document.addEventListener('composer-expand-request', handler);
    return () => document.removeEventListener('composer-expand-request', handler);
  }, []);

  // Post-send cleanup: InputBar dispatches `composer-text-cleared` after a successful send.
  // 1. Collapse the expanded sheet so the chat is visible while the response streams in
  //    (BUG-3 from smoke test 2026-05-30: composer was staying expanded, covering the chat).
  // 2. Persist the collapsed state to composerStore so re-mount on session switch agrees.
  // 3. Reset userCollapsedRef so the next keystroke can re-trigger auto-expand.
  useEffect(() => {
    const handler = () => {
      userCollapsedRef.current = false;
      setExpanded(false);
      onExpandedChange?.(false);
    };
    document.addEventListener('composer-text-cleared', handler);
    return () => document.removeEventListener('composer-text-cleared', handler);
  }, [onExpandedChange]);

  // Find-in-chat reuse (#1433): when find activates, drop any expanded compose
  // sheet so the bottom bar reads cleanly as a single-line find field (no scrim,
  // no drag handle, no attachment strip from a half-written message).
  useEffect(() => {
    if ((findActive || readAloudActive) && expanded) {
      setExpanded(false);
      onExpandedChange?.(false);
    }
  }, [findActive, readAloudActive, expanded, onExpandedChange]);

  const collapse = useCallback(() => {
    userCollapsedRef.current = true;
    setExpanded(false);
    onExpandedChange?.(false);
    // Tell InputBar to re-run autoResize so it snaps back to compact height
    document.dispatchEvent(new CustomEvent('composer-resize'));
  }, [onExpandedChange]);

  // Swipe-down-to-dismiss on the sheet handle/header (R-005). Track the start Y on
  // touchstart; if the user drags down past the threshold, collapse the sheet.
  const dragStartYRef = useRef<number | null>(null);
  const onSheetTouchStart = useCallback((e: TouchEvent) => {
    dragStartYRef.current = e.touches[0]?.clientY ?? null;
  }, []);
  const onSheetTouchEnd = useCallback((e: TouchEvent) => {
    const startY = dragStartYRef.current;
    dragStartYRef.current = null;
    if (startY == null) return;
    const endY = e.changedTouches[0]?.clientY ?? startY;
    if (endY - startY > 56) collapse(); // dragged down ≥56px → dismiss
  }, [collapse]);

  // ESC key collapses the expanded composer
  useEffect(() => {
    if (!expanded) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') collapse(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [expanded, collapse]);

  const handleAttach = useCallback(() => {
    if (onAttachTap) onAttachTap();
    closeDrawer();
  }, [onAttachTap, closeDrawer]);

  const handleRolePick = useCallback((key: string) => {
    if (!onRoleChange) return;
    onRoleChange(activeRole === key ? null : key);
    closeDrawer();
  }, [activeRole, onRoleChange, closeDrawer]);

  // ESC key closes drawer (desktop fallback; mobile back-gesture handled by overlay tap).
  // This effect runs independently of the collapse ESC handler above.
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [drawerOpen, closeDrawer]);

  return (
    <>
      {/* Semi-transparent scrim over the chat behind the expanded sheet — keeps the
          conversation faintly visible (R-008) and gives a tap-to-dismiss target (R-005).
          Rendered as a SIBLING of .bottom-composer (NOT portaled to body) so it shares
          .view-panel's stacking context: scrim z190 < composer z200, both fixed against
          the same containing block. A body portal would land in the root stacking context
          and risk painting over the composer (the will-change:transform trap, PR #831). */}
      {expanded && (
        <div
          class="composer-expand-scrim"
          onClick={collapse}
          aria-hidden="true"
        />
      )}
    <div
      ref={composerRef}
      class={`bottom-composer${busy ? ' bottom-composer--busy' : ''}${expanded ? ' bottom-composer--expanded' : ''}${findActive ? ' bottom-composer--find' : ''}${!findActive && readAloudActive ? ' bottom-composer--readaloud' : ''}`}
    >
      {/* Expanded-mode top bar: drag handle + collapse button + attach/mode drawer shortcut.
          Touch handlers enable swipe-down-to-dismiss. */}
      {expanded && (
        <div
          class="bottom-composer-expand-bar"
          onTouchStart={onSheetTouchStart}
          onTouchEnd={onSheetTouchEnd}
        >
          <div class="composer-drag-handle" aria-hidden="true" />
          <button
            type="button"
            class="composer-collapse-btn"
            onClick={collapse}
            aria-label="Collapse composer"
            title="Collapse (Esc)"
          >
            ↓
          </button>
          <button
            type="button"
            class={`composer-plus-btn composer-plus-btn--inline${drawerOpen ? ' composer-plus-btn--open' : ''}`}
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label="Open attach and mode drawer"
            aria-expanded={drawerOpen ? 'true' : 'false'}
            title="Attach or pick a mode"
          >
            +
          </button>
        </div>
      )}

      {/* Attachment strip is irrelevant while finding or reading aloud — hide it
          (#1433; Phase 5 extends to read-aloud). */}
      {composeMode && <AttachmentPreview
        attachments={attachments ?? []}
        processing={attachProcessing ?? false}
        error={attachError ?? null}
        onRemove={onRemoveAttachment ?? (() => {})}
        busy={busy}
      />}

      <div class="bottom-composer-row">
        {/* + button visible in collapsed compose mode only; expanded mode uses the
            top-bar button, and find / read-aloud modes replace the whole pill. */}
        {!expanded && composeMode && (
          <button
            type="button"
            class={`composer-plus-btn${drawerOpen ? ' composer-plus-btn--open' : ''}`}
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label="Open attach and mode drawer"
            aria-expanded={drawerOpen ? 'true' : 'false'}
            title="Attach or pick a mode"
          >
            +
          </button>
        )}
        {/* Single-tenant pill slot. Precedence (Q2=a): find (explicit user action)
            wins, then the read-aloud playback bar (Phase 5), then the normal InputBar.
            Hiding the read-aloud bar (barCollapsed) flips readAloudActive false in the
            shell → falls back to inputBar; narration keeps playing regardless. */}
        <div class="bottom-composer-input-wrap">{
          findActive && findBar ? findBar
          : readAloudActive && readAloudBar ? readAloudBar
          : inputBar
        }</div>
      </div>

      {drawerOpen && createPortal(
        <ComposerDrawer
          activeRole={activeRole ?? null}
          onAttach={onAttachTap ? handleAttach : null}
          onRolePick={onRoleChange ? handleRolePick : null}
          onClose={closeDrawer}
        />,
        document.body,
      )}
    </div>
    </>
  );
}

// ─── Drawer ─────────────────────────────────────────────────────────────

interface DrawerProps {
  activeRole: string | null;
  onAttach: (() => void) | null;
  onRolePick: ((key: string) => void) | null;
  onClose: () => void;
}

function ComposerDrawer({ activeRole, onAttach, onRolePick, onClose }: DrawerProps) {
  return (
    <div
      class="composer-drawer-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div class="composer-drawer" role="dialog" aria-label="Attach and mode">
        <div class="composer-drawer-handle" aria-hidden="true" />

        {onAttach && (
          <section class="composer-drawer-section" data-section="attach">
            <h3 class="composer-drawer-section-title">Attach</h3>
            <div class="composer-drawer-items">
              <button
                type="button"
                class="composer-drawer-item composer-drawer-item--attach"
                onClick={onAttach}
              >
                <span class="composer-drawer-item-icon" aria-hidden="true">{'📎'}</span>
                <span class="composer-drawer-item-label">Image or file</span>
              </button>
            </div>
          </section>
        )}

        {onRolePick && (
          <section class="composer-drawer-section" data-section="mode">
            <h3 class="composer-drawer-section-title">Mode</h3>
            <div class="composer-drawer-items composer-drawer-items--grid">
              {PREDEFINED_ROLES.map((role) => (
                <button
                  key={role.key}
                  type="button"
                  class={`composer-drawer-item composer-drawer-item--mode${activeRole === role.key ? ' active' : ''}`}
                  onClick={() => onRolePick(role.key)}
                  title={role.content}
                >
                  <span class="composer-drawer-item-icon" aria-hidden="true">{role.icon}</span>
                  <span class="composer-drawer-item-label">{role.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
